import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useCallback, useMemo, useState, type ReactNode } from 'react';

import { SessionExpiredError } from '@/lib/api/errors';
import { clearToken, readToken, writeToken } from '@/lib/session/token-store';

import { SessionContext, type Session, type SignOutReason } from './session-context';

const REASON_KEY = 'candidate-web.signout-reason';

function readReason(): SignOutReason | undefined {
  const r = window.sessionStorage.getItem(REASON_KEY);
  return r === 'expired' || r === 'user' ? r : undefined;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | undefined>(() => readToken());

  const signOut = useCallback((reason: SignOutReason = 'user') => {
    clearToken();
    window.sessionStorage.setItem(REASON_KEY, reason);
    setToken(undefined);
  }, []);

  const signIn = useCallback((next: string) => {
    writeToken(next);
    window.sessionStorage.removeItem(REASON_KEY);
    setToken(next);
  }, []);

  // One client per session: an expired token anywhere signs the user out, and the cache
  // (which holds their record) is dropped with the token.
  const queryClient = useMemo(() => {
    const onError = (error: unknown) => {
      if (error instanceof SessionExpiredError) signOut('expired');
    };
    return new QueryClient({
      queryCache: new QueryCache({ onError }),
      mutationCache: new MutationCache({ onError }),
      defaultOptions: {
        queries: { retry: false, refetchOnWindowFocus: false, staleTime: 60_000 },
        mutations: { retry: false },
      },
    });
  }, [signOut]);

  const value = useMemo<Session>(
    () => ({ token, signIn, signOut, signOutReason: readReason() }),
    [token, signIn, signOut],
  );

  return (
    <SessionContext.Provider value={value}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </SessionContext.Provider>
  );
}
